<template>
  <net-popup-menu @close="emit('close')">
    <template #title>{{ $t("objectNames.flowBranch") }}</template>

    <section>
      <h3 class="label mt-0">{{ $t('headings.flowBranchLimit') }}</h3>
      <input-switch
        :value="ctx.flowBranch.limitsFlow"
        :label="$t('labels.limitsFlow')"
        :label-right="true"
        @update:value="ctx.updateKey('limitsFlow', $event)"
      />
      <p class="message-info">
        <template v-if="ctx.flowBranch.branchType == 'source'">
          {{
            ctx.flowBranch.limitsFlow
              ? $t("labels.sourceBranchFlowLimited")
              : $t("labels.sourceBranchFlowUnlimited")
          }}
        </template>
        <template v-else>
          {{
            ctx.flowBranch.limitsFlow
              ? $t("labels.sinkBranchFlowLimited")
              : $t("labels.sinkBranchFlowUnlimited")
          }}
        </template>
      </p>
    </section>

    <!-- INFLUENCE CURVE -->
    <section>
      <h3 class="label mt-0">{{ $t('labels.strength') }}</h3>
      <div v-if="ctx.flowBranch.limitsFlow" class="flow-branch-menu__strength-simple mb-6">
        <input-number
          :value="branchStrength"
          :min="-Infinity"
          step="any"
          @update:value="branchStrength = $event"
        />
        * {{ $t("labels.flowValue") }} ({{ flowCalculatedValue.value }}{{ flowCalculatedValue.unit ? " " + flowCalculatedValue.unit : ""}})
      </div>

      <div class="column" style="position: relative;">
        <div class="flow-branch-menu__disabled" v-if="ctx.flowBranch.limitsFlow" :title="$t('texts.flowBranchCurveDisabled')"></div>
        <section>
          <div class="line-docanchor">
            <label class="label">{{ $t("labels.graph") }}</label>
            <documentation-anchor :object="ctx.flowBranch" prop="curve" />
          </div>
          <input-curve
            :value="Number(flowCalculatedValue.value)"
            :x-min="flowCalculatedValue.minValue"
            :x-max="flowCalculatedValue.maxValue"
            :point-list="ctx.flowBranch.curvePoints"
            :type="ctx.flowBranch.curveType"
            :formula="ctx.flowBranch.formula"
            class="mt-1"
            @change="onGraphChange"
            @update:formula="ctx.updateKey('formula', $event)"
            @hovervalue="updateCurveHover($event)"
          />
          <p>
            {{ $t("labels.xAxis") }}{{ $t(":") }}
            <i18n-t keypath="labels.valueOfX" tag="span" scope="global">
              <em>{{ flowTitle }}</em>
            </i18n-t>
            <br />
            {{ $t("labels.yAxis") }}{{ $t(":") }}
            <i18n-t keypath="labels.influenceOnX" tag="span" scope="global">
              <em>{{ ctx.element?.title || $t("labels.unnamed") }}</em>
            </i18n-t>
          </p>

          <i18n-t
            keypath="texts.influenceCurveConstantExample"
            tag="p"
            scope="global"
          >
            <template #startElement>
              <em>{{ flowTitle }}</em>
            </template>
            <template v-if="flowCalculatedValue.unit" #startUnit
              > {{ flowCalculatedValue.unit }}
            </template>
            <template #endElement>
              <em>{{ ctx.element?.title || $t("labels.unnamed") }}</em>
            </template>
            <template #startValue>{{ curveHover.x }}</template>
            <template #impulseValue>
              {{ $n(curveHover.y || 0, "displaySign") }}
            </template>
            <template v-if="ctx.element?.unit" #endUnit
              > {{ ctx.element?.unit }}
            </template>
          </i18n-t>
        </section>
      </div>
    </section>

    <button
      type="button"
      class="button button_secondary mv-2"
      @click="ctx.delete()"
    >
      {{ $t("actions.deleteFlowBranch", 1) }}
    </button>
  </net-popup-menu>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";

import NetPopupMenu from '@editor/net/NetPopupMenu.vue';
import InputSwitch from "@core/input/InputSwitch.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputCurve from "../relation/InputCurve.vue";
import DocumentationAnchor from "@editor/ui/DocumentationAnchor.vue";

import { useFlowStore } from "./flows.store";
import { useFlowBranch } from "./useFlowBranch";
import { useFlowBranchStore } from "./flowBranches.store";

import type { GraphPoint } from "../relation/GraphPoint";
import type { CurveFormType } from "../relation/RelationItem";

const props = defineProps<{
  flowBranchId: number;
}>();

const emit = defineEmits<{
  (e: "close"): void;
}>();

const flowStore = useFlowStore();
const flowBranchStore = useFlowBranchStore();
const ctx = useFlowBranch(() => props.flowBranchId);

const flowTitle = computed(() => flowStore.titles[ctx.flowBranch.flowId]);

function onGraphChange(data: { points: GraphPoint[]; type: CurveFormType }) {
  flowBranchStore.updateCurve({
    id: props.flowBranchId,
    curvePoints: data.points,
    curveType: data.type
  });
}

const flowCalculatedValue = computed(() => {
  if (!ctx.flow)
    return { minValue: 0, maxValue: 0, value: 0, unit: "", unitValid: true, valid: false };
  return flowStore.expressionValues[ctx.flow.uid];
});

const curveHover = reactive({
  x: 0,
  y: 0
});
function updateCurveHover(data: { x: number; y: number }) {
  curveHover.x = data.x;
  curveHover.y = data.y;
}

const branchStrength = computed({
  get: () => {
    const maxValue = flowCalculatedValue.value.maxValue;
    if (maxValue == null) return 1;
    return (
      (ctx.graphModel?.maxInfluence || 0) / maxValue
    );
  },
  set: (value: number) => {
    const formula = `${value}*x`;
    const newCurvePoints = ctx.getFormulaPoints(formula);
    ctx.update({
      formula,
      curvePoints: newCurvePoints,
      curveType: "linear"
    });
  }
});
</script>